import modalClose from '@brands/assets/icons/close.svg';
import ConsultationRichInput from '@brands/Components/Inputs/RichTextField/RichTextField';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import { useWindowSize } from '@brands/hooks';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { Button } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { TbExternalLink } from 'react-icons/tb';
import Modal, { Styles } from 'react-modal';
import CloseIcon from '@mui/icons-material/Close';
import styles from './styles.module.scss';

export interface EducationResource {
  url: string;
  title: string;
}

interface AddGoalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (goalText: string, educations: EducationResource[]) => void;
  initialGoalText?: string;
  initialEducations?: EducationResource[];
}

const AddGoalModal: FC<AddGoalModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialGoalText = '',
  initialEducations = [],
}) => {
  const screenSize = useWindowSize();
  const isMobile = useMemo(() => screenSize.width < 768, [screenSize.width]);

  const [goalText, setGoalText] = useState(initialGoalText);
  const [url, setUrl] = useState(initialEducations[0]?.url || '');
  const [title, setTitle] = useState(initialEducations[0]?.title || '');

  useEffect(() => {
    if (!isOpen) return;

    setGoalText(initialGoalText || '');
    setUrl(initialEducations[0]?.url || '');
    setTitle(initialEducations[0]?.title || '');
    // Only re-hydrate when the modal transitions to open — initialEducations is a
    // new array reference on every parent render, which would otherwise wipe
    // in-progress typing on every unrelated re-render while the modal stays open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const mobileStyles: Styles = {
    ...customStylesDashboard,
    overlay: {
      ...(customStylesDashboard.overlay || {}),
      overflowY: 'auto',
    },
    content: {
      ...(customStylesDashboard.content || {}),
      width: '100vw',
      height: '100vh',
      padding: '90px 16px 32px 16px',
      borderRadius: '0',
    },
  };

  const desktopStyles: Styles = {
    ...customStylesDashboard,
    content: {
      ...customStylesDashboard.content,
      width: '444px',
      padding: '30px 32px 32px',
      maxHeight: '750px',
    },
  };

  const normalizeUrl = (trimmedUrl?: string): string => {
    if (!trimmedUrl) return '';

    if (/^https?:\/\//i.test(trimmedUrl)) {
      return trimmedUrl;
    }

    return `https://${trimmedUrl}`;
  };

  const handleSave = (): void => {
    const trimmedGoalText = goalText?.trim?.() || '';
    const trimmedUrl = url.trim();
    const trimmedTitle = title.trim();

    if (!trimmedGoalText) {
      displayErrorDetails('Please add goal before saving');
      return;
    }

    if ((trimmedUrl && !trimmedTitle) || (!trimmedUrl && trimmedTitle)) {
      displayErrorDetails('Please fill both education URL and title');
      return;
    }
    const normalized = normalizeUrl(trimmedUrl);
    if (normalized) {
      try {
        new URL(normalized);
      } catch {
        displayErrorDetails('Please enter a valid URL');
        return;
      }
    }

    const educations: EducationResource[] =
      normalized && trimmedTitle
        ? [
            {
              url: normalized,
              title: trimmedTitle,
            },
          ]
        : [];

    onSave(trimmedGoalText, educations);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      style={isMobile ? mobileStyles : desktopStyles}
      contentLabel="Add Goal"
    >
      <div className={styles.AddGoalModal}>
        <div className={styles.bodyHead}>
          <CloseIcon onClick={onClose} />
        </div>

        <div className={`${styles.body} ${styles.formContainer}`}>
          <span className={styles.bodyHeader}>
            Add goal <span className={styles.required}>*</span>
          </span>

          <div className={styles.bodySub}>
            <ConsultationRichInput
              value={goalText}
              editable
              onChange={(html) => setGoalText(html)}
              placeholder="Add goal here"
              className={`fs-exclude ${styles.goalInput}`}
            />

            <div className={styles.sectionTitle}>Add education</div>

            <div className={styles.viewResources}>
              <div className={styles.viewResourcesContent}>
                <a
                  href="https://dentistry.one/resources/"
                  className={styles.resourceLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="fs-unmask">Open resource center</span>
                  <span className={`fs-unmask ${styles.viewResourcesIcon}`}>
                    <TbExternalLink />
                  </span>
                </a>
              </div>
            </div>

            <div className={styles.educationInput}>
              <TextField
                label="URL"
                name="goalEducationUrl"
                wrapperStyle={{ marginTop: '0', borderBottom: 'none' }}
                className={`fs-exclude ${styles.educationTextField}`}
                placeholder="https://"
                isUnderlined={false}
                multiline
                minRows={2}
                value={url}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
              />
            </div>

            <div className={styles.educationInput}>
              <TextField
                label="Title"
                name="goalEducationTitle"
                wrapperStyle={{ marginTop: '0', borderBottom: 'none' }}
                className={`fs-exclude ${styles.educationTextField}`}
                placeholder=""
                isUnderlined={false}
                multiline
                minRows={2}
                value={title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className={styles.bodyFooter}>
          <Button variant="contained" className={styles.containedBtn} onClick={handleSave}>
            {initialGoalText ? 'Edit goal':'Add goal'}
          </Button>

          <Button variant="text" className={styles.textBtn} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddGoalModal;
